import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/Primitives/Card"
import { Input } from "@/components/Primitives/Input"
import { Label } from "@/components/Primitives/Label"
import { Button } from "@/components/Primitives/Button"
import { Text } from "@/components/Primitives/Text"
import { useConfig } from "@/hooks/useConfig"
import { MarkdownRenderer } from "@/components/Primitives/MarkdownRenderer"
import { z } from "zod/v4"
import { askForPasswordReset } from "@/lib/api/auth"
import { AuthLayout } from "@/components/Primitives/AuthLayout"

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const { t } = useConfig()
  const [email, setEmail] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setError("")
      const emailSchema = z
        .email(t("config_forgot_password_error_email_invalid"))
        .min(5, t("config_forgot_password_error_email_short"))
        .max(100, t("config_forgot_password_error_email_long"))
      const result = emailSchema.safeParse(email)
      if (!result.success) {
        const issues = result.error.issues
        setError(
          issues[0]?.message || t("config_forgot_password_error_email_invalid"),
        )
        return
      }
      setIsSubmitting(true)
      await askForPasswordReset(email)
      navigate("/login/forgot/email")
    } catch (error: unknown) {
      console.error("Forgot password failed:", error)
      setError(t("config_forgot_password_error_generic"))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthLayout
      description={t("config_forgot_password_description")}
      imageUrl="/images/open-box.webp"
    >
      <Card>
        <CardHeader>
          <CardTitle>{t("config_forgot_password_title")}</CardTitle>
          <CardDescription>
            <MarkdownRenderer
              variant="small"
              className="text-neutral-700 [&>*]:mt-0"
              content={t("config_forgot_password_description")}
            />
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email" className="font-semibold">
                  {t("config_forgot_password_email_label")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="email"
                  placeholder={t("config_forgot_password_email_placeholder")}
                  value={email}
                  required
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full mt-6"
              data-testid="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t("config_forgot_password_button_submitting")
                : t("config_forgot_password_button_continue")}
            </Button>
            {error && (
              <Text variant="small" className="text-red-500 mt-2 text-center">
                {error}
              </Text>
            )}
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
